import nightIcon from '@/assets/icon-toggle/night.png';
import sunIcon from '@/assets/icon-toggle/sun.png';
import { CSS_COLORS } from '@/constants/pageStyles';
import { TYPOGRAPHY as SHARED_TYPOGRAPHY } from '@/constants/styles';
import { cn } from '@/lib/utils';
import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

const SWITCH = {
  widthExpanded: 44,
  widthCollapsed: 40,
  height: 22,
  thumbSize: 16,
  bgOn: CSS_COLORS.primary,
  bgOff: CSS_COLORS.border,
} as const;

const TYPOGRAPHY = {
  themeLabel: {
    fontFamily: SHARED_TYPOGRAPHY.fontFamily,
    fontWeight: SHARED_TYPOGRAPHY.fontWeight.semibold,
    fontSize: SHARED_TYPOGRAPHY.fontSize.xs,
    color: CSS_COLORS.mutedForeground,
  },
} as const;

interface ThemeSwitchProps {
  isLightMode: boolean;
  onToggle: () => void;
  isExpanded: boolean;
  className?: string;
}

export const ThemeSwitch = memo(function ThemeSwitch({
  isLightMode,
  onToggle,
  isExpanded,
  className,
}: ThemeSwitchProps) {
  const { t } = useTranslation();

  const trackStyle = useMemo(
    () => ({
      width: `${isExpanded ? SWITCH.widthExpanded : SWITCH.widthCollapsed}px`,
      height: `${SWITCH.height}px`,
      backgroundColor: isLightMode ? SWITCH.bgOn : SWITCH.bgOff,
    }),
    [isLightMode, isExpanded],
  );

  const thumbStyle = useMemo(() => {
    const width = isExpanded ? SWITCH.widthExpanded : SWITCH.widthCollapsed;
    return {
      width: `${SWITCH.thumbSize}px`,
      height: `${SWITCH.thumbSize}px`,
      transform: isLightMode
        ? `translateX(${width - SWITCH.thumbSize - 4}px)`
        : 'translateX(2px)',
    };
  }, [isLightMode, isExpanded]);

  return (
    <div
      className={cn(
        'flex items-center',
        isExpanded ? 'w-full justify-between' : 'justify-center',
        className,
      )}
    >
      {!!isExpanded && (
        <span style={TYPOGRAPHY.themeLabel}>{t('common.displayMode')}</span>
      )}
      <button
        type="button"
        onClick={onToggle}
        className="relative flex cursor-pointer items-center rounded-full transition-colors"
        style={trackStyle}
        title={isLightMode ? t('common.lightMode') : t('common.darkMode')}
        aria-label={t('common.displayMode')}
      >
        <span
          className="flex items-center justify-center rounded-full bg-white shadow-sm transition-transform duration-200"
          style={thumbStyle}
        >
          <img
            src={isLightMode ? sunIcon : nightIcon}
            alt={isLightMode ? t('common.lightMode') : t('common.darkMode')}
            className="h-3 w-3 object-contain"
          />
        </span>
      </button>
    </div>
  );
});
